/**
 * AnchoredSkillEffect — Conjuration Bind path Revival passive: faster
 * familiar intercepts.
 *
 * Always-on passive. Reduces the familiar's intercept cooldown from the
 * Fortify-established 2000ms baseline to 1200ms, letting the companion
 * step in front of attacks more frequently.
 *
 * The JSON value `interceptCooldownMs: 1200` is resolved through
 * UpgradeEffectResolver's `min` aggregation on the 'bind' scope —
 * lowest value wins, since a shorter cooldown is the better outcome.
 * When both Fortify (2000) and Anchored (1200) are unlocked, the
 * resolver returns 1200. The companion summon system reads this value
 * at spawn time to configure the familiar's intercept timing.
 *
 * Integration hook:
 *   - `getInterceptCooldownMs()` — returns the resolved intercept
 *     cooldown. The companion spawn system reads this instead of
 *     the Fortify default.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Anchored
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface AnchoredDeps {
  readonly resolver: {
    getMinEffect(skillId: string, key: string): number | undefined;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface AnchoredConfig {
  /**
   * Intercept cooldown in milliseconds. The min-aggregation resolver
   * picks the lowest value across all contributing nodes (Fortify = 2000,
   * Anchored = 1200). This config value serves as the node's contribution.
   */
  readonly interceptCooldownMs: number;
}

const DEFAULT_CONFIG: AnchoredConfig = {
  interceptCooldownMs: 1200,  // spec § Anchored: reduced from 2000ms (Fortify)
};

/** Fallback used when no resolver key is registered (pre-Fortify state). */
const FORTIFY_DEFAULT_COOLDOWN_MS = 2000;

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class AnchoredSkillEffect implements SkillEffect {
  readonly skillId = 'revival_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: AnchoredDeps;
  private readonly config: AnchoredConfig;

  // Observable state for simulation scenarios
  private _queriesCount = 0;

  constructor(deps: AnchoredDeps, config: Partial<AnchoredConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Anchored-specific API ---

  /**
   * Resolve the intercept cooldown in milliseconds.
   *
   * Returns the minimum value across all nodes contributing to
   * `interceptCooldownMs` in the 'bind' scope. When only Fortify is
   * unlocked, this returns 2000. When Anchored is also unlocked, it
   * returns 1200. A hypothetical future node could push it lower still.
   *
   * Falls back to the Fortify default (2000ms) if no resolver key is
   * registered — preserving correct behavior if the resolver hasn't
   * been seeded by any intercept-capable node.
   */
  getInterceptCooldownMs(): number {
    this._queriesCount++;
    return this.deps.resolver.getMinEffect('bind', 'interceptCooldownMs')
      ?? FORTIFY_DEFAULT_COOLDOWN_MS;
  }

  // --- State getters ---

  /** Number of times getInterceptCooldownMs() has been called. */
  get queriesCount(): number { return this._queriesCount; }

  /** The config-level cooldown value this node contributes. */
  get configCooldownMs(): number { return this.config.interceptCooldownMs; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // passive — not manually activated
  }

  activate(): boolean {
    return false;
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(_delta: number): void {
    // Passive — intercept cooldown is computed on demand
  }

  destroy(): void {
    // No resources to release
  }
}
